/**
 * Gestión de Equipaciones - Panel de Administración
 *
 * Este componente permite a los administradores:
 * - Ver todas las solicitudes de equipación
 * - Filtrar por talla, estado de pago, temporada
 * - Ver detalles de cada pedido incluyendo personalización
 * - Marcar equipaciones como entregadas
 * - Ver estadísticas de pedidos por talla
 */

import { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Alert, Spinner, Button, Form, Modal } from 'react-bootstrap';
import { FaTshirt, FaEye, FaCheck, FaArrowLeft, FaFileExcel, FaPlus } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import EquipacionUnifiedDataTable from '../../components/admin/EquipacionUnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import { toast } from 'react-toastify';
import equipacionService from '../../services/equipacionService';
import futbolistaService from '../../services/futbolistaService';
import torneoService from '../../services/torneoService';
import sedeService from '../../services/sedeService';
import * as XLSX from 'xlsx';
import './EquipacionManagement.css';

const EquipacionManagement = () => {
  const navigate = useNavigate();

  // Estados principales
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Estados para modales
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showRegistroModal, setShowRegistroModal] = useState(false);

  const [searchParams] = useSearchParams();
  const torneoNombreParam = searchParams.get('torneoNombre') || '';

  // Estados para filtros
  const [filters, setFilters] = useState({
    torneo: torneoNombreParam,
    talla: '',
    estado: '',
    page: 1,
    limit: 50
  });

  // Estados para opciones de filtros (datos reales de BD)
  const [filterOptions, setFilterOptions] = useState({
    torneos: [],
    tallas: [],
    estados: []
  });

  // Estados para el formulario de registro manual
  const [registroForm, setRegistroForm] = useState({
    futbolista_id: '',
    talla: '',
    personalizacion: '',
    torneo_id: '',
    sede_id: ''
  });
  const [futbolistas, setFutbolistas] = useState([]);
  const [torneos, setTorneos] = useState([]);
  const [sedesRegistro, setSedesRegistro] = useState([]);
  const [equipacionInfo, setEquipacionInfo] = useState(null);
  const [searchFutbolista, setSearchFutbolista] = useState('');
  const [loadingRegistro, setLoadingRegistro] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadSolicitudes();
    loadStats();
  }, [filters]);

  /**
   * Cargar opciones de filtros desde la base de datos
   */
  const loadFilterOptions = async () => {
    try {
      const response = await equipacionService.getFiltrosEquipacion();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (err) {
      console.error('Error al cargar opciones de filtros:', err);
    }
  };

  /**
   * Cargar solicitudes de equipación
   */
  const loadSolicitudes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await equipacionService.getSolicitudesEquipacion(filters);

      console.log('🔍 Respuesta completa del backend:', response);
      console.log('📋 Datos de solicitudes recibidos:', response.data?.solicitudes);
      console.log('🔢 Tipo de datos:', typeof response.data?.solicitudes);
      console.log('📊 Es array?:', Array.isArray(response.data?.solicitudes));

      if (response.success) {
        const solicitudesData = response.data.solicitudes || [];
        console.log('✅ Solicitudes a establecer:', solicitudesData);
        console.log('🔍 Primera solicitud:', solicitudesData[0]);
        setSolicitudes(solicitudesData);
      } else {
        throw new Error(response.message || 'Error al cargar solicitudes');
      }
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
      setError(err.message);
      toast.error('Error al cargar solicitudes de equipación');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cargar estadísticas de equipación
   */
  const loadStats = async () => {
    try {
      const response = await equipacionService.getResumenTallas();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  /**
   * Marcar equipación como entregada
   */
  const handleMarcarEntregada = async (solicitud) => {
    try {
      const response = await equipacionService.marcarEquipacionEntregada(
        solicitud.transaction_id,
        true
      );

      if (response.success) {
        toast.success('Equipación marcada como entregada');
        loadSolicitudes(); // Recargar datos
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.error('Error al marcar como entregada:', err);
      toast.error('Error al marcar equipación como entregada');
    }
  };

  /**
   * Exportar a Excel con FECHA, NOMBRE, TALLA y ESTADO
   * Sin duplicados - solo la última transacción de cada futbolista
   */
  const exportToExcel = () => {
    try {
      // Preparar datos para exportar con ESTADO
      const dataToExport = solicitudes.map(solicitud => ({
        'FECHA': new Date(solicitud.fecha_solicitud).toLocaleDateString('es-ES'),
        'NOMBRE': solicitud.futbolista_nombre,
        'TALLA': solicitud.talla,
        'ESTADO': solicitud.estado_pago === 'completed' ? 'Completado' :
                  solicitud.estado_pago === 'pending' ? 'Pendiente' :
                  solicitud.estado_pago === 'failed' ? 'Fallido' :
                  solicitud.estado_pago,
        'SEDE': (solicitud.sedes_equipacion || []).join(', ') || '—'
      }));

      // Crear hoja de cálculo
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Equipaciones');

      // Generar nombre de archivo con fecha actual
      const filename = `equipaciones_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(wb, filename);

      toast.success(`Archivo Excel exportado: ${solicitudes.length} equipaciones únicas`);
    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      toast.error('Error al exportar a Excel');
    }
  };

  /**
   * Abrir modal de detalles
   */
  const openDetailsModal = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setShowDetailsModal(true);
  };

  /**
   * Abrir modal de registro manual
   */
  const openRegistroModal = async () => {
    try {
      // Cargar información de equipación
      const equipacion = await equipacionService.getEquipacionInfo();
      setEquipacionInfo(equipacion);

      // Cargar torneos
      const torneosResponse = await torneoService.getTorneos();
      setTorneos(torneosResponse.data || []);

      // Pre-seleccionar torneo del filtro si existe
      let torneoIdPreseleccionado = '';
      if (filters.torneo) {
        const torneoFiltrado = (torneosResponse.data || []).find(t => t.nombre === filters.torneo);
        if (torneoFiltrado) {
          torneoIdPreseleccionado = torneoFiltrado.id;
        }
      }

      // Resetear formulario
      setRegistroForm({
        futbolista_id: '',
        talla: '',
        personalizacion: '',
        torneo_id: torneoIdPreseleccionado,
        sede_id: ''
      });
      setSearchFutbolista('');
      setFutbolistas([]);

      // Cargar sedes si hay torneo preseleccionado
      if (torneoIdPreseleccionado) {
        try {
          const sedesResp = await sedeService.getSedesPorTorneo(torneoIdPreseleccionado);
          setSedesRegistro(sedesResp.data || []);
        } catch (err) {
          console.error('Error al cargar sedes:', err);
          setSedesRegistro([]);
        }
      } else {
        setSedesRegistro([]);
      }

      setShowRegistroModal(true);
    } catch (err) {
      console.error('Error al abrir modal de registro:', err);
      toast.error('Error al cargar datos para el registro');
    }
  };

  /**
   * Buscar futbolistas por nombre
   */
  const handleSearchFutbolista = async (searchTerm) => {
    setSearchFutbolista(searchTerm);

    if (searchTerm.length < 2) {
      setFutbolistas([]);
      return;
    }

    try {
      console.log('🔍 Buscando futbolistas con término:', searchTerm);
      const response = await futbolistaService.searchFutbolistas(searchTerm);
      console.log('📋 Respuesta de búsqueda:', response);

      // Limitar a 20 resultados
      const resultados = (response.data || []).slice(0, 20);
      console.log('✅ Futbolistas encontrados:', resultados.length);

      setFutbolistas(resultados);
    } catch (err) {
      console.error('❌ Error al buscar futbolistas:', err);
      toast.error('Error al buscar futbolistas');
    }
  };

  /**
   * Seleccionar futbolista
   */
  const handleSelectFutbolista = (futbolista) => {
    setRegistroForm({ ...registroForm, futbolista_id: futbolista.id });
    setSearchFutbolista(futbolista.nombre);
    setFutbolistas([]);
  };

  /**
   * Registrar equipación manual
   */
  const handleRegistrarEquipacion = async () => {
    try {
      // Validar formulario
      if (!registroForm.futbolista_id) {
        toast.error('Debes seleccionar un futbolista');
        return;
      }
      if (!registroForm.talla) {
        toast.error('Debes seleccionar una talla');
        return;
      }
      if (!registroForm.torneo_id) {
        toast.error('Debes seleccionar un torneo');
        return;
      }
      if (!registroForm.sede_id) {
        toast.error('Debes seleccionar una sede');
        return;
      }

      setLoadingRegistro(true);

      const response = await equipacionService.registrarEquipacionManual({
        futbolista_id: registroForm.futbolista_id,
        talla: registroForm.talla,
        personalizacion: registroForm.personalizacion,
        torneo_id: registroForm.torneo_id,
        sede_id: registroForm.sede_id
      });

      if (response.success) {
        toast.success('Equipación registrada exitosamente');

        // Mostrar advertencia si ya tenía equipación
        if (response.data.ya_tenia_equipacion) {
          toast.warning('⚠️ Este futbolista ya tenía equipación registrada anteriormente');
        }

        setShowRegistroModal(false);
        loadSolicitudes(); // Recargar lista
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.error('Error al registrar equipación:', err);
      toast.error(err.response?.data?.message || 'Error al registrar equipación');
    } finally {
      setLoadingRegistro(false);
    }
  };

  /**
   * Configuración de columnas para la tabla
   */
  const columns = [
    {
      key: 'fecha_solicitud',
      label: 'Fecha',
      render: (row) => (
        <div className="date-info">
          <div className="created-date">
            {new Date(row.fecha_solicitud).toLocaleDateString('es-ES')}
          </div>
          <div className="created-time">
            {new Date(row.fecha_solicitud).toLocaleTimeString('es-ES')}
          </div>
        </div>
      )
    },
    {
      key: 'futbolista_nombre',
      label: 'Futbolista',
      render: (row) => (
        <div className="user-info">
          <div className="user-name">{row.futbolista_nombre}</div>
        </div>
      )
    },
    {
      key: 'talla',
      label: 'Talla',
      render: (row) => (
        <span className="status-badge status-info">
          {row.talla}
        </span>
      )
    },
    {
      key: 'personalizacion',
      label: 'Personalización',
      render: (row) => row.personalizacion ? (
        <span className="status-badge status-success">
          {row.personalizacion}
        </span>
      ) : (
        <span className="text-muted">Sin personalizar</span>
      )
    },
    {
      key: 'precio',
      label: 'Precio',
      render: (row) => (
        <div className="amount-info">
          <span className="amount">{row.precio}€</span>
        </div>
      )
    },
    {
      key: 'estado_pago',
      label: 'Estado',
      render: (row) => (
        <span className={`status-badge status-${
          row.estado_pago === 'completed' ? 'completed' :
          row.estado_pago === 'pending' ? 'pending' :
          row.estado_pago === 'failed' ? 'failed' : 'pending'
        }`}>
          {row.estado_pago === 'completed' ? 'Completado' :
           row.estado_pago === 'pending' ? 'Pendiente' :
           row.estado_pago === 'failed' ? 'Fallido' : row.estado_pago}
        </span>
      )
    },
    {
      key: 'torneo',
      label: 'Torneo',
      render: (row) => (
        <div className="description-info">
          <div className="main-description">{row.torneo}</div>
        </div>
      )
    },
    {
      key: 'sedes_equipacion',
      label: 'Sede equipación pagada',
      render: (row) => row.sedes_equipacion && row.sedes_equipacion.length > 0 ? (
        <div className="d-flex flex-wrap gap-1">
          {row.sedes_equipacion.map((sede, i) => (
            <span key={i} className="status-badge status-info">{sede}</span>
          ))}
        </div>
      ) : (
        <span className="text-muted">—</span>
      )
    }
  ];

  /**
   * Acciones disponibles para cada fila
   */
  const actions = [
    {
      label: 'Ver Detalles',
      icon: FaEye,
      variant: 'outline-primary',
      onClick: openDetailsModal
    },
    {
      label: 'Marcar Entregada',
      icon: FaCheck,
      variant: 'outline-success',
      onClick: handleMarcarEntregada,
      condition: (item) => item.estado_pago === 'completed'
    }
  ];

  if (loading && solicitudes.length === 0) {
    return (
      <AdminLayout>
        <div className="transfer-management">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transfer-management">
        <div className="page-header">
          <h1>
            <FaTshirt className="me-2" />
            Gestión de Equipaciones{torneoNombreParam ? ` — ${torneoNombreParam}` : ''}
          </h1>
          <p>Administra las solicitudes de equipación de los futbolistas</p>
        </div>

        {/* Botones de navegación y exportación */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
            <FaArrowLeft className="me-2" />Volver a Torneos
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={openRegistroModal}
            >
              <FaPlus className="me-2" />Registrar Equipación
            </Button>
            <Button
              variant="success"
              onClick={exportToExcel}
              disabled={solicitudes.length === 0}
            >
              <FaFileExcel className="me-2" />Exportar a Excel
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="admin-card">
          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="torneo-filter">Torneo:</label>
              <select
                id="torneo-filter"
                value={filters.torneo}
                onChange={(e) => setFilters({ ...filters, torneo: e.target.value, page: 1 })}
              >
                <option value="">Todos los torneos</option>
                {filterOptions.torneos.map(torneo => (
                  <option key={torneo} value={torneo}>{torneo}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="estado-filter">Estado de Pago:</label>
              <select
                id="estado-filter"
                value={filters.estado}
                onChange={(e) => setFilters({ ...filters, estado: e.target.value, page: 1 })}
              >
                <option value="">Todos (última transacción)</option>
                <option value="completed">Completado</option>
                <option value="pending">Pendiente</option>
                <option value="failed">Fallido</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="talla-filter">Talla:</label>
              <select
                id="talla-filter"
                value={filters.talla}
                onChange={(e) => setFilters({ ...filters, talla: e.target.value, page: 1 })}
              >
                <option value="">Todas las tallas</option>
                {filterOptions.tallas.map(talla => (
                  <option key={talla} value={talla}>{talla}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mb-4">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {/* Tabla de solicitudes */}
        <div className="admin-card">
          <EquipacionUnifiedDataTable
            data={solicitudes}
            columns={columns}
            actions={actions}
            loading={loading}
            emptyMessage="No hay solicitudes de equipación"
            pagination={true}
            searchable={true}
          />
        </div>

        {/* Modal de Detalles */}
        <StandardModal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Detalles de Solicitud de Equipación"
          size="large"
        >
          {selectedSolicitud && (
            <div className="equipacion-details">
              <Row>
                <Col md={6}>
                  <h6 className="text-primary mb-3">Información del Futbolista</h6>
                  <p><strong>Nombre:</strong> {selectedSolicitud.futbolista_nombre}</p>
                  <p><strong>Torneo:</strong> {selectedSolicitud.torneo}</p>
                  <p><strong>Fecha de Solicitud:</strong> {new Date(selectedSolicitud.fecha_solicitud).toLocaleString('es-ES')}</p>
                </Col>
                <Col md={6}>
                  <h6 className="text-primary mb-3">Detalles de la Equipación</h6>
                  <p><strong>Talla:</strong> <Badge bg="secondary">{selectedSolicitud.talla}</Badge></p>
                  <p><strong>Personalización:</strong> {
                    selectedSolicitud.personalizacion ?
                    <Badge bg="info">{selectedSolicitud.personalizacion}</Badge> :
                    <span className="text-muted">Sin personalizar</span>
                  }</p>
                  <p><strong>Precio:</strong> {selectedSolicitud.precio}€</p>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <h6 className="text-primary mb-3">Estado del Pedido</h6>
                  <p><strong>Estado de Pago:</strong> {' '}
                    <Badge bg={selectedSolicitud.estado_pago === 'completed' ? 'success' : 'warning'}>
                      {selectedSolicitud.estado_pago === 'completed' ? 'Pagado' : 'Pendiente'}
                    </Badge>
                  </p>
                  <p><strong>Monto Total:</strong> {selectedSolicitud.monto_total}€</p>
                  {selectedSolicitud.transaction_id && (
                    <p><strong>ID de Transacción:</strong> <code>{selectedSolicitud.transaction_id}</code></p>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </StandardModal>

        {/* Modal de Estadísticas */}
        <StandardModal
          isOpen={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          title="Estadísticas de Equipaciones"
          size="large"
        >
          {stats && (
            <div className="equipacion-stats">
              <h6 className="text-primary mb-3">Resumen por Tallas</h6>
              {stats.por_talla && stats.por_talla.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Talla</th>
                        <th>Cantidad</th>
                        <th>Precio Total</th>
                        <th>Futbolistas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.por_talla.map((item, index) => (
                        <tr key={index}>
                          <td><Badge bg="secondary">{item.talla}</Badge></td>
                          <td>{item.cantidad}</td>
                          <td>{item.precio_total}€</td>
                          <td>
                            {item.futbolistas.slice(0, 3).map((f, i) => (
                              <Badge key={i} bg="light" text="dark" className="me-1">
                                {f.nombre}
                              </Badge>
                            ))}
                            {item.futbolistas.length > 3 && (
                              <Badge bg="info">+{item.futbolistas.length - 3} más</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info">No hay estadísticas disponibles</Alert>
              )}

              {stats.total && (
                <div className="mt-4">
                  <h6 className="text-primary mb-3">Totales</h6>
                  <Row>
                    <Col md={4}>
                      <Card className="text-center">
                        <Card.Body>
                          <h4 className="text-primary">{stats.total.cantidad}</h4>
                          <p className="mb-0">Total Equipaciones</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="text-center">
                        <Card.Body>
                          <h4 className="text-success">{stats.total.precio_total}€</h4>
                          <p className="mb-0">Valor Total</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="text-center">
                        <Card.Body>
                          <h4 className="text-info">{stats.total.futbolistas_unicos}</h4>
                          <p className="mb-0">Futbolistas</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          )}
        </StandardModal>

        {/* Modal de Registro Manual */}
        <Modal
          show={showRegistroModal}
          onHide={() => setShowRegistroModal(false)}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title className="w-100">
              <FaPlus className="me-2" />
              Registrar Equipación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <Form>
              {/* Selector de Futbolista */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Futbolista *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={searchFutbolista}
                  onChange={(e) => handleSearchFutbolista(e.target.value)}
                  autoComplete="off"
                  size="sm"
                />
                {futbolistas.length > 0 && (
                  <div
                    className="list-group mt-2"
                    style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      fontSize: '0.9rem'
                    }}
                  >
                    {futbolistas.map(futbolista => (
                      <button
                        key={futbolista.id}
                        type="button"
                        className="list-group-item list-group-item-action py-2"
                        onClick={() => handleSelectFutbolista(futbolista)}
                      >
                        <div className="d-flex flex-column">
                          <span className="fw-bold">{futbolista.nombre}</span>
                          <small className="text-muted">
                            {futbolista.fecha_nacimiento ? new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES') : 'Sin fecha'}
                          </small>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.8rem' }}>
                  Escribe al menos 2 caracteres
                </Form.Text>
              </Form.Group>

              {/* Selector de Torneo */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Torneo *</Form.Label>
                <Form.Select
                  value={registroForm.torneo_id}
                  onChange={async (e) => {
                    const tid = e.target.value;
                    setRegistroForm({ ...registroForm, torneo_id: tid, sede_id: '' });
                    if (tid) {
                      try {
                        const sedesResp = await sedeService.getSedesPorTorneo(tid);
                        setSedesRegistro(sedesResp.data || []);
                      } catch (err) {
                        setSedesRegistro([]);
                      }
                    } else {
                      setSedesRegistro([]);
                    }
                  }}
                  size="sm"
                >
                  <option value="">Selecciona un torneo</option>
                  {torneos.map(torneo => (
                    <option key={torneo.id} value={torneo.id}>
                      {torneo.nombre}
                    </option>
                  ))}
                </Form.Select>
                {filters.torneo && (
                  <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.8rem' }}>
                    ✓ Pre-seleccionado del filtro
                  </Form.Text>
                )}
              </Form.Group>

              {/* Selector de Sede */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Sede *</Form.Label>
                <Form.Select
                  value={registroForm.sede_id}
                  onChange={(e) => setRegistroForm({ ...registroForm, sede_id: e.target.value })}
                  size="sm"
                  disabled={!registroForm.torneo_id}
                >
                  <option value="">{registroForm.torneo_id ? 'Selecciona una sede' : 'Selecciona primero un torneo'}</option>
                  {sedesRegistro.map(sede => (
                    <option key={sede.sede_id} value={sede.sede_id}>
                      {sede.sede_nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Selector de Talla */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Talla *</Form.Label>
                <Form.Select
                  value={registroForm.talla}
                  onChange={(e) => setRegistroForm({ ...registroForm, talla: e.target.value })}
                  size="sm"
                >
                  <option value="">Selecciona una talla</option>
                  {equipacionInfo?.tallas?.map(talla => (
                    <option key={talla} value={talla}>
                      {talla}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Personalización (opcional) */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Personalización</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre para camiseta (opcional)"
                  value={registroForm.personalizacion}
                  onChange={(e) => setRegistroForm({ ...registroForm, personalizacion: e.target.value })}
                  maxLength={20}
                  size="sm"
                />
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.8rem' }}>
                  Opcional - Máx. 20 caracteres
                </Form.Text>
              </Form.Group>

              {/* Información del precio */}
              {equipacionInfo && (
                <Alert variant="info" className="py-2 mb-0">
                  <div className="d-flex flex-column" style={{ fontSize: '0.9rem' }}>
                    <div><strong>Precio:</strong> {equipacionInfo.precio}€</div>
                    <div><strong>Marca:</strong> {equipacionInfo.marca}</div>
                    <div><strong>Incluye:</strong> {equipacionInfo.articulos?.join(', ')}</div>
                  </div>
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="secondary"
              onClick={() => setShowRegistroModal(false)}
              size="sm"
              className="flex-fill me-2"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleRegistrarEquipacion}
              disabled={loadingRegistro}
              size="sm"
              className="flex-fill"
            >
              {loadingRegistro ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Registrando...
                </>
              ) : (
                <>
                  <FaCheck className="me-1" />
                  Registrar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default EquipacionManagement;
